import "./styles.css";
import { createPortal } from "react-dom";
import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

export interface SortProductsPopupDetails {
    onSuccess?: () => void;
}

interface Props {
    onClose: () => void;
    details?: SortProductsPopupDetails;
}

interface Category {
    _id: string;
    name: string;
    picture: string | null;
    sort: number;
}

interface Product {
    _id: string;
    name: string;
    pictures: string[];
}

interface GhostInfo {
    product: Product;
    startY: number;
    x: number;
    width: number;
    height: number;
}

function DragHandle() {
    return (
        <svg width="12" height="16" viewBox="0 0 12 16" fill="none">
            <circle cx="4" cy="3" r="1.5" fill="currentColor" />
            <circle cx="8" cy="3" r="1.5" fill="currentColor" />
            <circle cx="4" cy="8" r="1.5" fill="currentColor" />
            <circle cx="8" cy="8" r="1.5" fill="currentColor" />
            <circle cx="4" cy="13" r="1.5" fill="currentColor" />
            <circle cx="8" cy="13" r="1.5" fill="currentColor" />
        </svg>
    );
}

export default function SortProductsPopup({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const p = t.products;
    const dir = lang === "ar" ? "rtl" : "ltr";

    // ── Step 1: pick a category ───────────────────────────────────────────────
    const [step, setStep] = useState<"pick" | "sort">("pick");
    const [pickedCategory, setPickedCategory] = useState<Category | null>(null);
    const [isAllProducts, setIsAllProducts] = useState(false);

    const { data: catData, loading: catLoading } = useFetch("products/categories", { query: { limit: 1000 } });
    const categories: Category[] = (catData?.data?.categories ?? []).slice().sort((a: Category, b: Category) => a.sort - b.sort);

    const handlePickAll = () => {
        setIsAllProducts(true);
        setPickedCategory(null);
        setStep("sort");
        fetchProducts({ query: { limit: 1000 } });
    };

    const handlePickCategory = (cat: Category) => {
        setIsAllProducts(false);
        setPickedCategory(cat);
        setStep("sort");
        fetchProducts({ query: { category: cat._id, limit: 1000 } });
    };

    const handleBack = () => {
        setStep("pick");
        setPickedCategory(null);
        setIsAllProducts(false);
        setItems([]);
        originalRef.current = [];
    };

    // ── Step 2: sort products in picked category ───────────────────────────────
    const { data: prodData, loading: prodLoading, invoke: fetchProducts } = useFetch("products", { method: "GET" }, false);

    const [items, setItems] = useState<Product[]>([]);
    const originalRef = useRef<Product[]>([]);

    useEffect(() => {
        if (!prodData) return;
        const prods: Product[] = prodData?.data?.products ?? [];
        setItems(prods);
        originalRef.current = prods;
    }, [prodData]);

    // ── FLIP animation ────────────────────────────────────────────────────────
    const itemElemsRef = useRef<Map<string, HTMLLIElement>>(new Map());
    const prevPositionsRef = useRef<Map<string, number>>(new Map());
    const needsFlipRef = useRef(false);

    const snapshotPositions = () => {
        const map = new Map<string, number>();
        itemElemsRef.current.forEach((el, id) => {
            map.set(id, el.getBoundingClientRect().top);
        });
        prevPositionsRef.current = map;
        needsFlipRef.current = true;
    };

    useLayoutEffect(() => {
        if (!needsFlipRef.current) return;
        needsFlipRef.current = false;

        const draggingItemId = dragRef.current !== null ? items[dragRef.current.index]?._id : null;

        itemElemsRef.current.forEach((el, id) => {
            if (id === draggingItemId) return;

            const prevTop = prevPositionsRef.current.get(id);
            if (prevTop === undefined) return;

            const newTop = el.getBoundingClientRect().top;
            const delta = prevTop - newTop;
            if (Math.abs(delta) < 1) return;

            el.style.transition = "none";
            el.style.transform = `translateY(${delta}px)`;
            el.offsetHeight; // eslint-disable-line @typescript-eslint/no-unused-expressions
            el.style.transition = "transform 0.18s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
            el.style.transform = "";
        });
    }, [items]);

    // ── Drag ──────────────────────────────────────────────────────────────────
    const dragRef = useRef<{
        index: number;
        offsetY: number;
        pointerId: number;
    } | null>(null);

    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [ghostInfo, setGhostInfo] = useState<GhostInfo | null>(null);
    const ghostRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const onHandlePointerDown = (e: React.PointerEvent<HTMLElement>, index: number) => {
        e.preventDefault();
        const li = (e.currentTarget as HTMLElement).closest<HTMLLIElement>("li");
        if (!li || !listRef.current) return;

        const rect = li.getBoundingClientRect();

        // eslint-disable-next-line react-hooks/immutability
        dragRef.current = {
            index,
            offsetY: e.clientY - rect.top,
            pointerId: e.pointerId,
        };

        listRef.current.setPointerCapture(e.pointerId);
        setDraggingId(items[index]._id);
        setGhostInfo({
            product: items[index],
            startY: rect.top,
            x: rect.left,
            width: rect.width,
            height: rect.height,
        });
    };

    const onListPointerMove = (e: React.PointerEvent<HTMLUListElement>) => {
        const drag = dragRef.current;
        if (!drag || !listRef.current) return;

        if (ghostRef.current) {
            ghostRef.current.style.top = `${e.clientY - drag.offsetY}px`;
        }

        const lis = listRef.current.querySelectorAll<HTMLLIElement>(":scope > li");
        let newIndex = lis.length - 1;
        for (let i = 0; i < lis.length; i++) {
            const r = lis[i].getBoundingClientRect();
            if (e.clientY < r.top + r.height / 2) {
                newIndex = i;
                break;
            }
        }

        if (newIndex !== drag.index) {
            snapshotPositions();
            const from = drag.index;
            setItems((prev) => {
                const arr = [...prev];
                const [moved] = arr.splice(from, 1);
                arr.splice(newIndex, 0, moved);
                return arr;
            });
            // eslint-disable-next-line react-hooks/immutability
            drag.index = newIndex;
        }
    };

    const stopDrag = () => {
        if (dragRef.current && listRef.current) {
            try {
                listRef.current.releasePointerCapture(dragRef.current.pointerId);
            } catch {
                // pointer may already be released
            }
        }
        // eslint-disable-next-line react-hooks/immutability
        dragRef.current = null;
        setDraggingId(null);
        setGhostInfo(null);
    };

    const handleReset = () => {
        snapshotPositions();
        setItems([...originalRef.current]);
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const { loading: submitting, status: submitStatus, invoke: submitInvoke } = useFetch("products/sort", { method: "PUT" }, false);

    const wasSubmitting = useRef(false);
    useEffect(() => {
        if (submitting) {
            wasSubmitting.current = true;
        } else if (wasSubmitting.current) {
            wasSubmitting.current = false;
            if (submitStatus === 200) {
                details?.onSuccess?.();
                onClose();
            }
        }
    }, [submitting]);

    const handleConfirm = () => {
        if (items.length === 0) return;
        submitInvoke({
            data: {
                ...(pickedCategory ? { categoryId: pickedCategory._id } : {}),
                productIds: items.map((prod) => prod._id),
            },
        });
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="spp-popup" dir={dir}>
            {/* Header */}
            <div className="spp-header">
                {step === "sort" && (
                    <button type="button" className="spp-back" onClick={handleBack} disabled={submitting}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path
                                d={dir === "rtl" ? "M7 4l5 5-5 5" : "M11 4L6 9l5 5"}
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                )}
                <div className="spp-header-text">
                    <h2 className="spp-title">{p.sortPopupTitle}</h2>
                    {step === "sort" && <span className="spp-subtitle">{isAllProducts ? p.sortAll : pickedCategory?.name}</span>}
                </div>
                <button type="button" className="spp-close" onClick={onClose}>
                    ×
                </button>
            </div>

            {/* Body */}
            <div className="spp-body">
                {/* ── Step 1: pick category ── */}
                {step === "pick" && (
                    <>
                        <p className="spp-hint">{p.sortStep1Hint}</p>
                        {catLoading ? (
                            <div className="spp-loader">
                                <span className="spp-loader-spinner" />
                            </div>
                        ) : (
                            <ul className="spp-cat-list">
                                <li>
                                    <button type="button" className="spp-cat-item spp-cat-item--all" onClick={handlePickAll}>
                                        <div className="spp-item-img spp-item-img--all">
                                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                <rect x="2" y="2" width="6" height="6" rx="1.5" fill="currentColor" />
                                                <rect x="10" y="2" width="6" height="6" rx="1.5" fill="currentColor" />
                                                <rect x="2" y="10" width="6" height="6" rx="1.5" fill="currentColor" />
                                                <rect x="10" y="10" width="6" height="6" rx="1.5" fill="currentColor" />
                                            </svg>
                                        </div>
                                        <span className="spp-item-name">{p.sortAll}</span>
                                        <svg className="spp-cat-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <path
                                                d={dir === "rtl" ? "M10 4L6 8l4 4" : "M6 4l4 4-4 4"}
                                                stroke="currentColor"
                                                strokeWidth="1.6"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </button>
                                </li>
                                {categories.map((cat) => (
                                    <li key={cat._id}>
                                        <button type="button" className="spp-cat-item" onClick={() => handlePickCategory(cat)}>
                                            <div className="spp-item-img">
                                                {cat.picture ? (
                                                    <img src={cat.picture} alt={cat.name} />
                                                ) : (
                                                    <div className="spp-item-img-placeholder" />
                                                )}
                                            </div>
                                            <span className="spp-item-name">{cat.name}</span>
                                            <svg className="spp-cat-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path
                                                    d={dir === "rtl" ? "M10 4L6 8l4 4" : "M6 4l4 4-4 4"}
                                                    stroke="currentColor"
                                                    strokeWidth="1.6"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}

                {/* ── Step 2: sort products ── */}
                {step === "sort" && (
                    <>
                        <p className="spp-hint">{p.sortStep2Hint}</p>
                        {prodLoading ? (
                            <div className="spp-loader">
                                <span className="spp-loader-spinner" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="spp-empty">{p.sortNoProducts}</div>
                        ) : (
                            <ul
                                ref={listRef}
                                className="spp-list"
                                onPointerMove={onListPointerMove}
                                onPointerUp={stopDrag}
                                onPointerCancel={stopDrag}
                            >
                                {items.map((item, index) => (
                                    <li
                                        key={item._id}
                                        ref={(el) => {
                                            if (el) itemElemsRef.current.set(item._id, el);
                                            else itemElemsRef.current.delete(item._id);
                                        }}
                                        className={`spp-item${draggingId === item._id ? " spp-item--placeholder" : ""}`}
                                    >
                                        <div className="spp-item-handle" onPointerDown={(e) => onHandlePointerDown(e, index)}>
                                            <DragHandle />
                                        </div>
                                        <div className="spp-item-img">
                                            {item.pictures?.[0] ? (
                                                <img src={item.pictures[0]} alt={item.name} />
                                            ) : (
                                                <div className="spp-item-img-placeholder" />
                                            )}
                                        </div>
                                        <span className="spp-item-name">{item.name}</span>
                                        <span className="spp-item-order">{index + 1}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>

            {/* Footer */}
            <div className="spp-footer">
                {step === "pick" ? (
                    <button type="button" className="spp-btn spp-btn--cancel" onClick={onClose}>
                        {p.sortCancel}
                    </button>
                ) : (
                    <>
                        <button
                            type="button"
                            className="spp-btn spp-btn--reset"
                            onClick={handleReset}
                            disabled={submitting || prodLoading || items.length === 0}
                        >
                            {p.sortReset}
                        </button>
                        <button type="button" className="spp-btn spp-btn--cancel" onClick={handleBack} disabled={submitting}>
                            {p.sortBack}
                        </button>
                        <button
                            type="button"
                            className="spp-btn spp-btn--submit"
                            onClick={handleConfirm}
                            disabled={submitting || prodLoading || items.length === 0}
                        >
                            {submitting ? <span className="spp-spinner" /> : p.sortConfirm}
                        </button>
                    </>
                )}
            </div>

            {/* Ghost — portalled to body so popup overflow never clips it */}
            {ghostInfo &&
                createPortal(
                    <div
                        ref={ghostRef}
                        className="spp-ghost"
                        style={{
                            position: "fixed",
                            left: ghostInfo.x,
                            top: ghostInfo.startY,
                            width: ghostInfo.width,
                            height: ghostInfo.height,
                            pointerEvents: "none",
                            zIndex: 99999,
                        }}
                    >
                        <div className="spp-ghost-card">
                            <div className="spp-item-handle spp-item-handle--active">
                                <DragHandle />
                            </div>
                            <div className="spp-item-img">
                                {ghostInfo.product.pictures?.[0] ? (
                                    <img src={ghostInfo.product.pictures[0]} alt={ghostInfo.product.name} />
                                ) : (
                                    <div className="spp-item-img-placeholder" />
                                )}
                            </div>
                            <span className="spp-item-name">{ghostInfo.product.name}</span>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
